/**
 * CalendarShareDialog — Vague 113 (Livraison 2)
 *
 * Two-section modal:
 *   1. "Partager mon agenda" — pick teammates to grant read-access to my calendar.
 *   2. "Agendas partagés avec moi" — toggle the visibility of each incoming share
 *      on the main calendar, or revoke the access entirely.
 *
 * The toggle state is persisted in calendar_user_settings.visible_owner_ids so
 * the user keeps the same layout across devices.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Check, UserPlus, Eye, EyeOff, X, Users } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';

function Avatar({ user, size = 28 }) {
  const initial = (user?.name || user?.email || '?').trim().charAt(0).toUpperCase();
  if (user?.picture) {
    return <img src={user.picture} alt="" className="rounded-full object-cover" style={{ width: size, height: size }} />;
  }
  return (
    <div className="rounded-full bg-[#EEF2FF] text-[#4F46E5] font-bold flex items-center justify-center"
         style={{ width: size, height: size, fontSize: size * 0.45 }}>
      {initial}
    </div>
  );
}

export default function CalendarShareDialog({ open, onClose, visibleOwnerIds, onVisibleChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teammates, setTeammates] = useState([]);  // [{user_id,name,email,picture,role}]
  const [outgoing, setOutgoing] = useState([]);    // shares I gave
  const [incoming, setIncoming] = useState([]);    // shares I received

  const reload = async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get('/calendar/shareable-users'),
        api.get('/calendar/shares'),
      ]);
      setTeammates(t.data || []);
      setOutgoing(s.data?.outgoing || []);
      setIncoming(s.data?.incoming || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de charger les partages' });
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open) reload(); /* eslint-disable-next-line */ }, [open]);

  // Who I've already shared with → set of user_ids.
  const sharedWithIds = useMemo(() => new Set(outgoing.map((s) => s.recipient?.user_id)), [outgoing]);

  const shareWith = async (member) => {
    setSaving(true);
    try {
      await api.post('/calendar/shares', { shared_with_user_id: member.user_id });
      toast({ title: 'Partagé', description: `Agenda partagé avec ${member.name}` });
      reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec du partage' });
    } finally { setSaving(false); }
  };

  const unshareWith = async (share) => {
    if (!window.confirm(`Retirer l'accès de ${share.recipient?.name} à votre agenda ?`)) return;
    setSaving(true);
    try {
      await api.delete(`/calendar/shares/${share.id}`);
      toast({ title: 'Retiré', description: 'Accès révoqué.' });
      reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const revokeIncoming = async (share) => {
    if (!window.confirm(`Vous ne verrez plus l'agenda de ${share.owner?.name}. Confirmer ?`)) return;
    setSaving(true);
    try {
      await api.delete(`/calendar/shares/${share.id}`);
      // Also untoggle from visibility.
      const next = (visibleOwnerIds || []).filter((uid) => uid !== share.owner?.user_id);
      onVisibleChange?.(next);
      toast({ title: 'Retiré' });
      reload();
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Échec' });
    } finally { setSaving(false); }
  };

  const toggleVisible = (ownerId) => {
    const current = new Set(visibleOwnerIds || []);
    if (current.has(ownerId)) current.delete(ownerId);
    else current.add(ownerId);
    onVisibleChange?.(Array.from(current));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="cal-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-4 h-4 text-[#4F46E5]" /> Partage d'agenda</DialogTitle>
          <DialogDescription>
            Donnez à vos collègues un accès lecture à votre agenda, et choisissez les agendas partagés que vous souhaitez voir.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-[#8E8FA5]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…
          </div>
        ) : (
          <div className="space-y-5">
            {/* Section 1 — Share my agenda */}
            <section data-testid="cal-share-outgoing">
              <div className="text-[11.5px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2 flex items-center gap-1">
                <UserPlus className="w-3 h-3" /> Partager mon agenda avec
              </div>
              {teammates.length === 0 ? (
                <div className="text-[12px] text-[#8E8FA5] py-3">Aucun collègue dans votre organisation.</div>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {teammates.map((m) => {
                    const active = sharedWithIds.has(m.user_id);
                    const share = outgoing.find((s) => s.recipient?.user_id === m.user_id);
                    return (
                      <div key={m.user_id} data-testid={`cal-share-member-${m.user_id}`}
                           className="flex items-center gap-3 p-2 rounded-lg border border-[#EFF0F6] hover:bg-[#FAFAFE]">
                        <Avatar user={m} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{m.name}</div>
                          <div className="text-[11px] text-[#8E8FA5] truncate">{m.email}{m.role ? ` · ${m.role}` : ''}</div>
                        </div>
                        {active ? (
                          <button onClick={() => unshareWith(share)} disabled={saving}
                                  data-testid={`cal-share-revoke-${m.user_id}`}
                                  className="h-8 px-2.5 rounded-lg border border-[#FCA5A5] text-[#B91C1C] text-[11.5px] font-semibold flex items-center gap-1 hover:bg-[#FEF2F2] disabled:opacity-60">
                            <Check className="w-3 h-3" /> Partagé
                          </button>
                        ) : (
                          <button onClick={() => shareWith(m)} disabled={saving}
                                  data-testid={`cal-share-add-${m.user_id}`}
                                  className="h-8 px-2.5 rounded-lg brand-gradient text-white text-[11.5px] font-semibold flex items-center gap-1 disabled:opacity-60">
                            <UserPlus className="w-3 h-3" /> Partager
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Section 2 — Incoming shared agendas */}
            <section data-testid="cal-share-incoming">
              <div className="text-[11.5px] font-bold text-[#4F46E5] uppercase tracking-wide mb-2 flex items-center gap-1">
                <Eye className="w-3 h-3" /> Agendas partagés avec moi
              </div>
              {incoming.length === 0 ? (
                <div className="text-[12px] text-[#8E8FA5] py-3">Aucun collègue ne partage encore son agenda avec vous.</div>
              ) : (
                <div className="space-y-1.5">
                  {incoming.map((s) => {
                    const isVisible = (visibleOwnerIds || []).includes(s.owner?.user_id);
                    return (
                      <div key={s.id} className="flex items-center gap-3 p-2 rounded-lg border border-[#EFF0F6]"
                           data-testid={`cal-incoming-${s.owner?.user_id}`}>
                        <Avatar user={s.owner} />
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1A1B2E] truncate">{s.owner?.name}</div>
                          <div className="text-[11px] text-[#8E8FA5] truncate">{s.owner?.email}</div>
                        </div>
                        <button onClick={() => toggleVisible(s.owner?.user_id)}
                                data-testid={`cal-toggle-visible-${s.owner?.user_id}`}
                                className={`h-8 px-2.5 rounded-lg text-[11.5px] font-semibold flex items-center gap-1 border ${isVisible ? 'bg-[#4F46E5] text-white border-[#4F46E5]' : 'bg-white text-[#6B6C85] border-[#EFF0F6] hover:border-[#C7D2FE]'}`}>
                          {isVisible ? <><Eye className="w-3 h-3" /> Affiché</> : <><EyeOff className="w-3 h-3" /> Masqué</>}
                        </button>
                        <button onClick={() => revokeIncoming(s)} disabled={saving}
                                title="Retirer l'accès" data-testid={`cal-revoke-incoming-${s.owner?.user_id}`}
                                className="h-8 w-8 rounded-lg border border-[#EFF0F6] text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center disabled:opacity-60">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        )}

        <DialogFooter>
          <button onClick={onClose} data-testid="cal-share-close"
                  className="h-10 px-4 rounded-xl border border-[#EFF0F6] text-[13px] font-semibold">
            Fermer
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
